/**
 * iter86.77.5 / iter86.77.6 — API-NFL (american-football) poller cadence card.
 *
 * Mirrors the API-Football cadence card: site admins tune the NFL
 * background poller without redeploying. Blank field = env/module
 * default. Applies on the next poller wake.
 *
 * Pro plan (7500/day). The poller is live-window aware: while any game
 * is in progress (or within 1h of kickoff) it switches to the LIVE
 * interval + games TTL for near-real-time scores. Odds lines refresh
 * every 8h (~3×/day), hourly on game day.
 */
import { useEffect, useState } from "react";
import { RefreshCw, Save, RotateCcw, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const fmtSec = (n) => {
    if (n == null) return "—";
    if (n < 60) return `${n}s`;
    if (n < 3600) return `${Math.round(n / 60)}m`;
    return `${Math.round(n / 3600)}h`;
};

const FIELDS = [
    {
        key: "poll_interval_sec", label: "Poll interval (sec)",
        help: "How often the poller wakes outside live windows. Floor 120s.",
        testid: "nfl-cadence-interval",
    },
    {
        key: "games_ttl_sec", label: "Games TTL (sec)",
        help: "Schedule + scores cache freshness outside live windows. Floor 120s.",
        testid: "nfl-cadence-games-ttl",
    },
    {
        key: "standings_ttl_sec", label: "Standings TTL (sec)",
        help: "W-L-T records cache freshness. Floor 120s.",
        testid: "nfl-cadence-standings-ttl",
    },
    {
        key: "live_poll_interval_sec", label: "LIVE poll interval (sec)",
        help: "Wake interval while a game is in progress or <1h from kickoff. Floor 60s.",
        testid: "nfl-cadence-live-interval",
    },
    {
        key: "live_games_ttl_sec", label: "LIVE games TTL (sec)",
        help: "Score freshness during live windows. Floor 60s.",
        testid: "nfl-cadence-live-games-ttl",
    },
];

export default function NflPollerCadenceCard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState({ poll_interval_sec: "", games_ttl_sec: "", standings_ttl_sec: "", live_poll_interval_sec: "", live_games_ttl_sec: "" });

    const reload = async () => {
        setLoading(true);
        try {
            const d = await api.nflGetPollerCadence();
            setData(d);
            const next = {};
            for (const f of FIELDS) next[f.key] = d.overrides.includes(f.key) ? String(d[f.key]) : "";
            setDraft(next);
        } catch (e) {
            toast.error("Failed to load NFL poller cadence");
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); }, []);

    const onSave = async () => {
        setSaving(true);
        try {
            const body = {};
            for (const f of FIELDS) {
                const v = (draft[f.key] || "").toString().trim();
                body[f.key] = v === "" ? null : Number(v);
                if (body[f.key] !== null && (!Number.isInteger(body[f.key]) || body[f.key] <= 0)) {
                    toast.error(`${f.key} must be a positive integer`);
                    setSaving(false); return;
                }
            }
            const d = await api.nflUpdatePollerCadence(body);
            toast.success("NFL cadence saved — applies on the next poller wake");
            setData(prev => ({ ...prev, ...d }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    const eff = (key) => (draft[key] ? Number(draft[key]) : (data?.defaults?.[key] || 0));
    const interval = eff("poll_interval_sec");
    const liveInterval = eff("live_poll_interval_sec");
    // Dark day: games + standings at each wake past their TTLs. Live day
    // (~14h of game windows/wk on Sun+Thu+Mon): live-interval score syncs.
    const dailyMax = interval
        ? Math.round(86400 / Math.max(interval, eff("games_ttl_sec") || interval))
          + Math.round(86400 / Math.max(interval, eff("standings_ttl_sec") || interval))
        : null;
    const liveBurst = liveInterval ? Math.round((10 * 3600) / liveInterval) : null;

    return (
        <div className="brutal-card p-4" data-testid="nfl-poller-cadence-card">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-zinc-100 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-volt" /> API-NFL Football Poller Cadence
                </h3>
                <button
                    onClick={reload}
                    disabled={loading}
                    className="text-xs text-zinc-400 hover:text-white flex items-center gap-1"
                    data-testid="nfl-poller-cadence-refresh"
                >
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Reload
                </button>
            </div>

            {!data ? (
                <p className="text-xs text-zinc-500">Loading…</p>
            ) : (
                <>
                    <p className="text-xs text-zinc-400 mb-4">
                        Tune how often the poller hits API-American-Football (NFL season sync +
                        weekly spreads). Leave a field blank to use the default. The poller is{" "}
                        <strong className="text-zinc-200">live-window aware</strong>: while a game is in
                        progress (or &lt;1h from kickoff) it uses the LIVE knobs. Odds lines refresh every
                        8h (~3×/day), hourly on game day. Changes apply on the <strong>next</strong> wake
                        (within <span className="font-mono">{fmtSec(data.poll_interval_sec)}</span>).
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                        {FIELDS.map((f) => (
                            <CadenceField
                                key={f.key}
                                label={f.label}
                                help={f.help}
                                value={draft[f.key]}
                                placeholder={String(data.defaults[f.key])}
                                onChange={(v) => setDraft(p => ({ ...p, [f.key]: v }))}
                                onRevert={() => setDraft(p => ({ ...p, [f.key]: "" }))}
                                isOverridden={data.overrides.includes(f.key)}
                                current={data[f.key]}
                                testid={f.testid}
                            />
                        ))}
                    </div>

                    {dailyMax !== null && (
                        <div className="text-xs text-zinc-400 mb-4 p-2 bg-zinc-950/50 border border-zinc-800 rounded">
                            <strong className="text-zinc-200">Estimated upstream calls:</strong>{" "}
                            <span className="font-mono text-volt">~{dailyMax}/day</span>{" "}
                            <span className="text-zinc-500">
                                dark (wake every {fmtSec(interval)})
                                {liveBurst ? <> + ~<span className="font-mono">{liveBurst}</span> per 10h game-day live window @ {fmtSec(liveInterval)}</> : null}
                                ; odds add ~3/game/day. Pro plan: 7500/day.
                            </span>
                        </div>
                    )}

                    <button
                        onClick={onSave}
                        disabled={saving}
                        className="px-3 py-1.5 bg-volt/20 border border-volt text-volt rounded text-xs font-bold uppercase tracking-wider flex items-center gap-2 hover:bg-volt/30 disabled:opacity-50"
                        data-testid="nfl-poller-cadence-save"
                    >
                        <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save Cadence"}
                    </button>
                </>
            )}
        </div>
    );
}

function CadenceField({ label, help, value, placeholder, onChange, onRevert, isOverridden, current, testid }) {
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between">
                <label className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</label>
                {isOverridden && (
                    <button
                        onClick={onRevert}
                        title="Revert to default"
                        className="text-[9px] text-blaze hover:text-blaze/80 flex items-center gap-1"
                        data-testid={`${testid}-revert`}
                    >
                        <RotateCcw className="w-2.5 h-2.5" /> revert
                    </button>
                )}
            </div>
            <input
                type="number"
                min="1"
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="w-full px-2 py-1 bg-zinc-950 border border-zinc-800 rounded text-xs font-mono text-white focus:border-volt focus:outline-none"
                data-testid={testid}
            />
            <p className="text-[10px] text-zinc-500">
                {help} Current: <span className="font-mono text-zinc-300">{current}</span>
                {isOverridden ? <span className="text-blaze ml-1">(override)</span> : <span className="text-zinc-600 ml-1">(default)</span>}
            </p>
        </div>
    );
}
